import { defineStore } from 'pinia';
import { notificationsService } from '@/serviceHandlers/notificationsService';
import { shiftsService } from '@/serviceHandlers/shiftsService';
import { timeOffRequestsService } from '@/serviceHandlers/timeOffRequestsService';
import { scheduleChangeRequestsService } from '@/serviceHandlers/scheduleChangeRequestsService';
import { conversationsService } from '@/serviceHandlers/conversationsService';
import { useLoginStore } from '@/stores/loginStore';

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    notifications: [],
    pendingAuthAlerts: [],
    pendingShiftApprovals: 0,
    pendingTimeOffRequests: 0,
    pendingScheduleChangeRequests: 0,
    pendingComplianceItems: 0,
    unreadMessages: 0,
    loading: false,
    lastUpdate: null
  }),

  getters: {
    totalNotificationCount(state) {
      const loginStore = useLoginStore();
      const authAlertCount = loginStore.user?.hide_email_auth_tracking ? 0 : state.pendingAuthAlerts.length;
      const shiftApprovalCount = state.pendingShiftApprovals > 0 ? 1 : 0;
      const notificationCount = state.notifications.length;

      return authAlertCount + shiftApprovalCount + notificationCount + state.pendingComplianceItems;
    },

    allNotifications(state) {
      const notifications = [];

      // Add authorization alerts
      state.pendingAuthAlerts.forEach(alert => {
        notifications.push({
          id: alert.id,
          type: 'auth-alert',
          title: `Automated Authorization Request - ${alert.metadata.clientName} - ${alert.metadata.serviceCode || 'Service'}`,
          message: '',
          created_at: alert.created_at,
          priority: 'high',
          originalData: alert
        });
      });

      // Add shift approval notifications
      if (state.pendingShiftApprovals > 0) {
        notifications.push({
          id: 'shift-approvals',
          type: 'shift-approval',
          title: 'Shifts Pending Approval',
          message: `${state.pendingShiftApprovals} shift${state.pendingShiftApprovals > 1 ? 's' : ''} require${state.pendingShiftApprovals === 1 ? 's' : ''} your approval`,
          created_at: new Date(),
          priority: 'medium'
        });
      }

      // Add other notifications
      state.notifications.forEach(notification => {
        notifications.push({
          ...notification,
          type: notification.type || 'info',
          priority: 'low'
        });
      });

      // Sort by priority (high, medium, low) then by created_at (newest first)
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return notifications.sort((a, b) => {
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }
  },

  actions: {
    async loadNotifications() {
      this.loading = true;
      try {
        const loginStore = useLoginStore();
        const userType = loginStore.user?.user_type;
        const isAdminOrLead = userType === 'Admin' || userType === 'Lead';

        // Build promises array based on user type
        const promises = [
          notificationsService.getUnread().catch(() => []),
          this.getUnreadMessagesCount(),
        ];

        // Only load authorization alerts and shift approvals for Admins and Leads
        if (isAdminOrLead) {
          promises.push(
            notificationsService.getPendingAuthorizationAlerts().catch(() => []),
            this.getTodayShifts()
          );
        }

        // Load pending time off requests for admins
        // Note: schedule change requests count is set by HomeDashboard.loadScheduleChangeRequests()
        if (userType === 'Admin') {
          promises.push(
            this.getPendingTimeOffRequests()
          );
        }

        const results = await Promise.all(promises);

        // Parse results based on what was loaded
        let resultIndex = 0;
        this.notifications = results[resultIndex++];
        this.unreadMessages = results[resultIndex++];

        if (isAdminOrLead) {
          const authAlerts = results[resultIndex++];
          const todayShifts = results[resultIndex++];

          // Process auth alerts
          this.pendingAuthAlerts = authAlerts.map(alert => ({
            ...alert,
            metadata: typeof alert.metadata === 'string' ? JSON.parse(alert.metadata) : alert.metadata,
            processing: false
          }));

          // Calculate pending approvals from today's shifts
          this.pendingShiftApprovals = todayShifts.filter(
            s => s.clockOut && s.shift_status !== 'approved'
          ).length;
        } else {
          // Clear these for non-admin/lead users
          this.pendingAuthAlerts = [];
          this.pendingShiftApprovals = 0;
        }

        if (userType === 'Admin') {
          const timeOffCount = results[resultIndex++];
          this.pendingTimeOffRequests = timeOffCount;

          // Note: pendingScheduleChangeRequests is set by HomeDashboard.loadScheduleChangeRequests()
          // Note: pendingComplianceItems is set by HomeDashboard.loadComplianceSummary()
          // Both avoid duplicate API calls
        } else {
          this.pendingTimeOffRequests = 0;
          this.pendingScheduleChangeRequests = 0;
        }

        this.lastUpdate = new Date();
      } catch (error) {
        // Error loading notifications
      } finally {
        this.loading = false;
      }
    },

    async getTodayShifts() {
      // Query the LOCAL day's real boundaries as UTC instants. Building the
      // window from toISOString's date is doubly wrong: after ~6pm Denver the
      // UTC date is tomorrow, and T00:00Z-T23:59Z is the UTC day, so evening
      // local shifts fall outside "today" either way.
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      const params = {
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString(),
        userType: "Admin",
        userId: 1,
        perPage: 1000,
      };
      const response = await shiftsService.getByRole(params);
      return response.shifts || [];
    },

    async getPendingTimeOffRequests() {
      try {
        const params = { status: 'pending' };
        const response = await timeOffRequestsService.list(params);
        // Handle new paginated response format
        // Return the total count from pagination, not just the current page items
        if (response && response.pagination) {
          return response.pagination.total;
        }
        // Fallback for non-paginated response
        if (response && response.data) {
          return response.data.length;
        }
        return Array.isArray(response) ? response.length : 0;
      } catch (error) {
        return 0;
      }
    },

    async getPendingScheduleChangeRequests() {
      try {
        const params = { status: 'pending' };
        const response = await scheduleChangeRequestsService.list(params);
        // Handle new paginated response format
        // Return the total count from pagination, not just the current page items
        if (response && response.pagination) {
          return response.pagination.total;
        }
        // Fallback for non-paginated response
        if (response && response.data) {
          return response.data.length;
        }
        return Array.isArray(response) ? response.length : 0;
      } catch (error) {
        return 0;
      }
    },

    async markNotificationAsRead(notificationId) {
      try {
        await notificationsService.markAsRead(notificationId);
        this.notifications = this.notifications.filter(n => n.id !== notificationId);
      } catch (error) {
        // Error marking notification as read
      }
    },

    removeAuthAlert(alertId) {
      this.pendingAuthAlerts = this.pendingAuthAlerts.filter(a => a.id !== alertId);
    },

    updatePendingShiftApprovals(count) {
      this.pendingShiftApprovals = count;
    },

    async getUnreadMessagesCount() {
      try {
        const response = await conversationsService.getUnreadCount();
        return response.unreadCount || 0;
      } catch (error) {
        return 0;
      }
    },

    updateUnreadMessages(count) {
      this.unreadMessages = count;
    }
  }
});